export const rfqArticles = [
    {
        id: 'rfq.overview',
        title: 'RFQ Overview',
        category: 'RFQs',
        order: 1,
        summary: 'Request for Quote - getting pricing from suppliers',
        content: `
The RFQ (Request for Quote) page lets you request pricing from multiple suppliers for your BOQ items. This streamlines the quoting process by sending standardised requests and collecting responses in one place.

**RFQ workflow:**

1. **Create** — pick a job and cost centre, the BOQ or catalogue items, and the suppliers. Set the date quotes are due by.
2. **Send** — every supplier with an email address gets the request and a personal link. The send dialog names any supplier without an email so you can send them the link by hand.
3. **Receive** — suppliers enter unit prices (ex-tax) on the link, or tell you they are not quoting. You get an in-app notice for each answer, a reminder goes to unanswered suppliers two days before the due date, and you are told on the due date who is still missing.
4. **Compare** — the comparison tab ranks each line and totals every quote from its lines.
5. **Award** — one supplier wins; the RFQ closes, every other link stops working, the winner's prices become their supplier prices and the job's un-ordered BOQ lines for those items switch to them.

**Rules worth knowing:**

- Links close at the end of the due date, on award and on cancel. Change the due date on a sent RFQ to reopen or extend.
- Your catalogue estimate is **hidden from suppliers by default** (they quote to the number they see). Change the default in Settings → Suppliers, or per RFQ.
- All prices on an RFQ are unit prices **excluding** your workspace's tax (GST unless you renamed it).
- Anyone except a Viewer can create, edit and send RFQs and record quotes. Awarding, cancelling, applying prices and reading a supplier's quote with the AI reader need the owner, admin or manager role.
    `,
        expandableSections: [
            {
                title: 'Creating an RFQ',
                content: `
1. Go to the **RFQs** page
2. Click **New RFQ**
3. Select a **Job** and **Cost Centre**
4. Choose items to include:
   - Tick individual items, or
   - Use **Select All** to include everything
5. Add one or more **Suppliers** to quote
6. Set a **Due Date** for responses
7. Click **Create RFQ**

**Tips:**
- Group related items together for clearer supplier quotes
- Set realistic due dates (7-14 days recommended)
- Include notes for suppliers about specific requirements
        `,
            },
            {
                title: 'Sending RFQs to Suppliers',
                content: `
Once created, send the RFQ to suppliers:

1. Open the RFQ from the grid and check the items and suppliers
2. Click **Send RFQ**. If any supplier has no email address the dialog says so before you confirm
3. Each supplier with an email receives the request — items, quantities, units, the due date and their personal link. Replies to that email come to you, not to the platform

**After sending you can still:**

- **Copy link** on a supplier's card — creates a fresh link (their earlier one stops working) and copies it, for suppliers without email or whose email bounced
- **Resend** — a fresh link by email
- **Add & send** another supplier from the Suppliers tab — they are emailed at once
- **Change** the due date from the summary — the links stay open to the end of the new date

Items and the other details are frozen once sent; cancel and create a new RFQ to change them.
        `,
            },
            {
                title: 'Reviewing & Applying Quotes',
                content: `
As suppliers respond you get an in-app notice per quote (and per "not quoting"). Open the RFQ:

1. The Suppliers tab shows each supplier's status, whether their email went, and their quoted total — always the sum of their lines
2. **Record Quote** enters a quote that arrived by phone or email (or drop the supplier's PDF on the AI reader to prefill it)
3. The **Comparison** tab shows every quoted line side by side, ex-tax, with the lowest unit price marked
4. **Award** the supplier you choose. The dialog tells you what will change; afterwards the RFQ is completed, the other suppliers are marked unsuccessful and their links stop working

**What an award does:** the winner's line prices are recorded as their supplier prices (a price equal to their current one is not duplicated), and on a job RFQ the job's un-ordered BOQ lines for those items switch to this supplier and price. The toast reports how many prices were recorded and how many BOQ lines changed.

**Apply Prices** on a quoted (not awarded) supplier records their prices as supplier prices without awarding — useful for a price check.

A quote received after an award or after the due date is refused at the link; the supplier sees why.
        `,
            },
        ],
        keywords: ['rfq', 'request for quote', 'quotation', 'supplier pricing', 'tender', 'quote'],
        route: '/rfqs',
        relatedArticles: ['rfq.supplier-portal', 'suppliers.overview', 'boq.suppliers'],
        screenshots: [
            {
                src: 'rfq/rfq-create.png',
                alt: 'New RFQ dialog',
                caption: 'Creating a request for quote — pick the job and items, then the suppliers to invite',
            },
        ],
    },
    {
        id: 'rfq.supplier-portal',
        title: 'Supplier Response Portal',
        category: 'RFQs',
        order: 2,
        summary: 'How suppliers respond to your RFQ requests',
        content: `
When you send an RFQ, each supplier receives a personal link. No account or login is required.

**What suppliers see:**

- Your company name and logo, the RFQ number and the date quotes are due by
- Each item with its description, quantity and unit — and your estimate only if you chose to show it
- A unit price field per item (ex-tax, labelled with your tax name), an optional SKU, and notes
- The total, worked out from their lines
- A **Not quoting this one** button, so you hear "no" instead of silence

**How suppliers respond:**

1. Click the link in the email
2. Enter unit prices for the items they can supply (leave the rest blank)
3. Add notes, a quote validity date if they want, and submit — or decline

The quote is recorded at once, the sender is notified in the app, and the supplier can reopen the link to update their quote until the RFQ closes.
    `,
        expandableSections: [
            {
                title: 'Portal Security',
                content: `
Each supplier link is:
- **Unique** — one link per supplier per RFQ; a new link (Copy link / Resend) cancels the old one
- **Unguessable and stored hashed** — the link itself is never kept on the server and is not shown to staff; staff create a fresh one when they need it
- **Scoped** — it only ever shows that RFQ's items to that supplier
- **Closing** — it stops taking quotes at the end of the due date, when the RFQ is awarded, and when it is cancelled; the supplier then sees a "this request has closed" page with your contact details
- **Rate-limited** — the public endpoints are throttled per address
- **No login required** — suppliers do not need a Costed account
        `,
            },
        ],
        keywords: ['supplier portal', 'response', 'pricing', 'quote submission'],
        route: '/rfqs',
        relatedArticles: ['rfq.overview'],
    },
];
